/**
 * Custom hooks for bottle operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bottleService } from '../services/bottleService';

/**
 * Hook to fetch paginated bottles
 * @param {Object} options - Query options
 * @returns {Object} Query result with data, loading, and error states
 */
export function useBottles(options = {}) {
  const { page = 1, limit = 20, sortBy = 'created_at', sortOrder = 'desc', filters = {} } = options;

  return useQuery({
    queryKey: ['bottles', { page, limit, sortBy, sortOrder, filters }],
    queryFn: () => bottleService.getBottles({ page, limit, sortBy, sortOrder, filters }),
    placeholderData: (previousData) => previousData
  });
}

/**
 * Hook to fetch distinct store pick values for filter dropdowns
 * @returns {Object} Query result with sorted list of store names
 */
export function useStorePicks() {
  return useQuery({
    queryKey: ['bottles', 'storePicks'],
    queryFn: () => bottleService.getStorePicks(),
    staleTime: 1000 * 60 * 5
  });
}

/**
 * Hook to fetch a single bottle
 * @param {string} id - Bottle UUID
 * @returns {Object} Query result with bottle data
 */
export function useBottle(id) {
  return useQuery({
    queryKey: ['bottle', id],
    queryFn: () => bottleService.getBottleById(id),
    enabled: !!id
  });
}

/**
 * Hook to create a new bottle
 * @returns {Object} Mutation result with mutate function
 */
export function useCreateBottle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bottleService.createBottle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bottles'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });
}

/**
 * Hook to update an existing bottle
 * @returns {Object} Mutation result with mutate function
 */
export function useUpdateBottle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }) => bottleService.updateBottle(id, updates),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bottles'] });
      queryClient.invalidateQueries({ queryKey: ['bottle', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });
}

/**
 * Hook to delete a bottle
 * @returns {Object} Mutation result with mutate function
 */
export function useDeleteBottle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bottleService.deleteBottle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bottles'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });
}
